import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

const styles = {
  dialogPaper: {
    borderRadius: "12px",
    maxWidth: "556px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: "14px 24px",
    borderBottom: "1px solid #EAEAEA",
  },
  title: {
    fontWeight: "600",
    color: "rgba(47, 43, 61, 0.9)",
    lineHeight: "28px",
    fontSize: "18px",
  },
  closeIcon: {
    color: "rgba(62, 63, 76, 1)",
    fontSize: "24px",
  },
  dialogContent: {
    p: "24px",
  },
  heading: {
    mb: "16px",
    fontSize: "18px",
    fontWeight: 500,
    color: "rgba(47, 43, 61, 0.9)",
  },
  description: {
    fontSize: "15px",
    color: "rgba(47, 43, 61, 0.9)",
  },
  dialogActions: {
    justifyContent: "space-between",
    gap: "10px",
    flexDirection: { xs: "column", sm: "row" },
    p: "12px 24px",
  },
  cancelButton: {
    backgroundColor: "rgba(128, 131, 144, 1)",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "500",
    color: "white",
    textTransform: "none",
    width: { xs: "100%", sm: "auto" },
    boxShadow: "none !important",
    "&:hover": {
      backgroundColor: "rgba(128, 131, 144, 0.9)",
      boxShadow: "none",
    },
  },
  confirmButton: {
    ml: "0px !important",
    width: { xs: "100%", sm: "auto" },
    textTransform: "none",
    color: "rgba(255, 255, 255, 1)",
    fontWeight: 500,
    fontSize: "15px",
    borderRadius: "8px",
    px: "16px",
    py: "6px",
    backgroundColor: "rgba(183, 72, 74, 1)",
    borderColor: "rgba(183, 72, 74, 1)",
    boxShadow: "none !important",
    "&:hover": {
      backgroundColor: "rgba(183, 72, 74, 0.9)",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(183, 72, 74, 0.4)",
      color: "rgba(255, 255, 255, 0.7)",
    },
  },
};

const PauseProductDialog = ({ open, onClose, onConfirm, loading, isActive }) => {
  const { t } = useTranslation("supplier-products");

  const isPausing = isActive;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      disableEscapeKeyDown
      maxWidth="556px"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <Box sx={styles.header}>
        <Typography sx={styles.title}>
          {isPausing ? t("pause_dialog.title") : t("unpause_dialog.title")}
        </Typography>
        <IconButton sx={{ mr: "-10px" }} onClick={onClose}>
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>
      </Box>

      <DialogContent sx={styles.dialogContent}>
        <Typography sx={styles.heading}>
          {isPausing
            ? t("pause_dialog.are_you_sure")
            : t("unpause_dialog.are_you_sure")}
        </Typography>
        <Typography sx={styles.description}>
          {isPausing
            ? t("pause_dialog.description")
            : t("unpause_dialog.description")}
        </Typography>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={styles.cancelButton}
        >
          {isPausing ? t("pause_dialog.cancel") : t("unpause_dialog.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          sx={styles.confirmButton}
        >
          {isPausing ? t("pause_dialog.confirm") : t("unpause_dialog.confirm")}
          {loading && (
            <CircularProgress
              size={20}
              color="rgba(255, 255, 255, 0.7)"
              sx={{ ml: "8px" }}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PauseProductDialog;
